import { Navigate, NavLink, Route, Routes, useLocation, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import SellersList from './pages/SellersList';
import SellerNew from './pages/SellerNew';
import SellerEdit from './pages/SellerEdit';
import SchedulesList from './pages/SchedulesList';
import ScheduleEdit from './pages/ScheduleEdit';
import ProductsList from './pages/ProductsList';
import ProductEdit from './pages/ProductEdit';
import PriceConfigsList from './pages/PriceConfigsList';
import PriceConfigEdit from './pages/PriceConfigEdit';
import PriceImports from './pages/PriceImports';
import PriceWorkspace from './pages/PriceWorkspace';
import PublishLog from './pages/PublishLog';
import Workspace from './pages/Workspace';
import MyProfile from './pages/MyProfile';

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="container"><p className="muted">Загрузка…</p></div>;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
}

function RequireRole({ role, children }: { role: 'admin' | 'seller'; children: JSX.Element }) {
  const { user } = useAuth();
  if (!user || user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function Shell({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';
  return (
    <>
      <header className="topbar">
        <Link className="brand" to={isAdmin ? '/' : '/workspace'}>B2B Цветы</Link>
        <nav className="topnav">
          {isAdmin ? (
            <>
              <NavLink to="/" end>Главная</NavLink>
              <NavLink to="/sellers">Продавцы</NavLink>
              <NavLink to="/schedules">Расписания</NavLink>
              <NavLink to="/products">Товары</NavLink>
              <NavLink to="/prices">Прайсы</NavLink>
              <NavLink to="/publish-log">Лог</NavLink>
            </>
          ) : (
            // Seller has a single home: their workspace. Sub-nav lives there.
            <NavLink to="/workspace" end={false}>Мой кабинет</NavLink>
          )}
        </nav>
        <div className="userbox">
          <span>{user?.login} · {user?.role === 'admin' ? 'админ' : 'продавец'}</span>
          <button type="button" className="logout" onClick={() => logout()}>Выйти</button>
        </div>
      </header>
      <main>{children}</main>
    </>
  );
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user?.role === 'seller') return <Navigate to="/workspace" replace />;
  return <Dashboard />;
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <Shell>
                <Routes>
                  <Route path="/" element={<HomeRedirect />} />

                  {/* Admin-only routes */}
                  <Route path="/sellers" element={<RequireRole role="admin"><SellersList /></RequireRole>} />
                  <Route path="/sellers/new" element={<RequireRole role="admin"><SellerNew /></RequireRole>} />
                  <Route path="/sellers/:id" element={<RequireRole role="admin"><SellerEdit /></RequireRole>} />

                  {/* Admin-mode pages (seller arrives here via /workspace/*) */}
                  <Route path="/schedules" element={<RequireRole role="admin"><SchedulesList /></RequireRole>} />
                  <Route path="/schedules/new" element={<RequireRole role="admin"><ScheduleEdit /></RequireRole>} />
                  <Route path="/schedules/:id" element={<RequireRole role="admin"><ScheduleEdit /></RequireRole>} />
                  <Route path="/products" element={<RequireRole role="admin"><ProductsList /></RequireRole>} />
                  <Route path="/products/new" element={<RequireRole role="admin"><ProductEdit /></RequireRole>} />
                  <Route path="/products/:id" element={<RequireRole role="admin"><ProductEdit /></RequireRole>} />
                  {/* New unified Prices page (AI flow). Old /prices/imports + /prices/configs stay as aliases. */}
                  <Route path="/prices" element={<RequireRole role="admin"><PriceWorkspace /></RequireRole>} />
                  <Route path="/prices/imports" element={<RequireRole role="admin"><PriceImports /></RequireRole>} />
                  <Route path="/prices/configs" element={<RequireRole role="admin"><PriceConfigsList /></RequireRole>} />
                  <Route path="/prices/configs/new" element={<RequireRole role="admin"><PriceConfigEdit /></RequireRole>} />
                  <Route path="/prices/configs/:id" element={<RequireRole role="admin"><PriceConfigEdit /></RequireRole>} />
                  <Route path="/publish-log" element={<RequireRole role="admin"><PublishLog /></RequireRole>} />

                  {/* Seller workspace: nested tab routes. Reuses the same
                      list/edit pages — they already scope by req.user.id on
                      the backend, so the seller only sees their own data. */}
                  <Route path="/workspace" element={<Workspace />}>
                    <Route index element={<Navigate to="profile" replace />} />
                    <Route path="profile" element={<MyProfile />} />
                    <Route path="products" element={<ProductsList />} />
                    <Route path="products/new" element={<ProductEdit />} />
                    <Route path="products/:id" element={<ProductEdit />} />
                    <Route path="prices" element={<PriceWorkspace />} />
                    <Route path="prices/imports" element={<PriceImports />} />
                    <Route path="prices/configs" element={<PriceConfigsList />} />
                    <Route path="prices/configs/new" element={<PriceConfigEdit />} />
                    <Route path="prices/configs/:id" element={<PriceConfigEdit />} />
                    <Route path="schedules" element={<SchedulesList />} />
                    <Route path="schedules/new" element={<ScheduleEdit />} />
                    <Route path="schedules/:id" element={<ScheduleEdit />} />
                  </Route>

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Shell>
            </RequireAuth>
          }
        />
      </Routes>
    </AuthProvider>
  );
}
